import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { DollarSign, Plus, Loader2, AlertCircle, Clock, CheckCircle2, CircleDollarSign, Trash2, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { formatINR } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const paymentTypeLabels = {
  deposit: 'Deposit',
  installment: 'Installment',
  final: 'Final Payment',
  other: 'Other'
};

const paymentTypeColors = {
  deposit: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  installment: 'bg-secondary/10 text-secondary-foreground border-secondary/30',
  final: 'bg-primary/10 text-primary border-primary/20',
  other: 'bg-muted text-muted-foreground border-border'
};

export const PaymentsPage = () => {
  const [payments, setPayments] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    vendor_id: '',
    amount: '',
    due_date: '',
    description: '',
    payment_type: 'installment',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [pRes, vRes, sRes] = await Promise.all([
        axios.get(`${API_URL}/api/payments`, { withCredentials: true }),
        axios.get(`${API_URL}/api/vendors`, { withCredentials: true }),
        axios.get(`${API_URL}/api/payments/summary`, { withCredentials: true })
      ]);
      setPayments(pRes.data);
      setVendors(vRes.data);
      setSummary(sRes.data);
    } catch (error) {
      toast.error('Error loading payments');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.vendor_id) {
      toast.error('Please select a vendor');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/payments`, {
        ...form,
        amount: parseFloat(form.amount)
      }, { withCredentials: true });
      toast.success('Payment scheduled!');
      setDialogOpen(false);
      setForm({ vendor_id: '', amount: '', due_date: '', description: '', payment_type: 'installment', notes: '' });
      fetchData();
    } catch (error) {
      toast.error('Error adding payment');
    }
  };

  const togglePaid = async (paymentId, currentlyPaid) => {
    try {
      await axios.patch(`${API_URL}/api/payments/${paymentId}`, { paid: !currentlyPaid }, { withCredentials: true });
      toast.success(currentlyPaid ? 'Payment unmarked' : 'Payment marked as paid!');
      fetchData();
    } catch (error) {
      toast.error('Error updating payment');
    }
  };

  const deletePayment = async (paymentId) => {
    try {
      await axios.delete(`${API_URL}/api/payments/${paymentId}`, { withCredentials: true });
      toast.success('Payment removed');
      fetchData();
    } catch (error) {
      toast.error('Error deleting payment');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const today = new Date().toISOString().split('T')[0];
  const weekFromNow = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  const overdue = payments.filter(p => !p.paid && p.due_date < today);
  const dueThisWeek = payments.filter(p => !p.paid && p.due_date >= today && p.due_date <= weekFromNow);
  const upcoming = payments.filter(p => !p.paid && p.due_date > weekFromNow);
  const paid = payments.filter(p => p.paid);

  const renderPayment = (payment) => {
    const isOverdue = !payment.paid && payment.due_date < today;
    return (
      <Card key={payment.id} className={`shadow-sm ${payment.paid ? 'opacity-70' : isOverdue ? 'border-destructive/30 bg-destructive/5' : ''}`} data-testid="payment-card">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h3 className="font-medium">{payment.description}</h3>
                <span className={`text-xs px-2 py-0.5 rounded-full border ${paymentTypeColors[payment.payment_type] || paymentTypeColors.other}`}>
                  {paymentTypeLabels[payment.payment_type] || 'Other'}
                </span>
                {isOverdue && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-destructive text-destructive-foreground font-medium">
                    Overdue
                  </span>
                )}
              </div>
              <div className="text-sm text-muted-foreground mb-1">
                <span className="font-medium text-foreground">{payment.vendor_name}</span>
              </div>
              <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  Due: {new Date(payment.due_date).toLocaleDateString()}
                </span>
                {payment.paid && payment.paid_date && (
                  <span className="flex items-center gap-1 text-green-600">
                    <CheckCircle2 className="h-3 w-3" />
                    Paid: {new Date(payment.paid_date).toLocaleDateString()}
                  </span>
                )}
              </div>
              {payment.notes && <p className="text-sm text-muted-foreground mt-2">{payment.notes}</p>}
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="text-xl font-serif font-bold text-primary">{formatINR(payment.amount, { compact: true })}</div>
              </div>
              <Button
                variant={payment.paid ? "outline" : "default"}
                size="sm"
                onClick={() => togglePaid(payment.id, payment.paid)}
                data-testid="toggle-paid-button"
                className="rounded-full"
              >
                {payment.paid ? 'Mark Unpaid' : 'Mark Paid'}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => deletePayment(payment.id)}
                data-testid="delete-payment-button"
                className="text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-serif font-bold tracking-tight">Vendor Payments</h1>
          <p className="text-muted-foreground mt-1">Track deposits & installments, synced to budget</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="add-payment-button" disabled={vendors.length === 0}>
              <Plus className="h-4 w-4 mr-2" />
              Schedule Payment
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Schedule a Payment</DialogTitle>
              <DialogDescription>Add a deposit, installment, or final payment to a vendor</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Vendor</Label>
                <Select value={form.vendor_id} onValueChange={(val) => setForm({...form, vendor_id: val})}>
                  <SelectTrigger data-testid="payment-vendor-select">
                    <SelectValue placeholder="Select a vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.name} - {v.category}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  placeholder="e.g., 30% Deposit, Balance Payment"
                  value={form.description}
                  onChange={(e) => setForm({...form, description: e.target.value})}
                  required
                  data-testid="payment-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Amount (₹)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={form.amount}
                    onChange={(e) => setForm({...form, amount: e.target.value})}
                    required
                    data-testid="payment-amount-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    value={form.due_date}
                    onChange={(e) => setForm({...form, due_date: e.target.value})}
                    required
                    data-testid="payment-due-date-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={form.payment_type} onValueChange={(val) => setForm({...form, payment_type: val})}>
                  <SelectTrigger data-testid="payment-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="deposit">Deposit</SelectItem>
                    <SelectItem value="installment">Installment</SelectItem>
                    <SelectItem value="final">Final Payment</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  placeholder="Additional notes..."
                  value={form.notes}
                  onChange={(e) => setForm({...form, notes: e.target.value})}
                  data-testid="payment-notes-input"
                />
              </div>
              <Button type="submit" className="w-full rounded-full" data-testid="submit-payment-button">
                Schedule Payment
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-6 md:grid-cols-4">
        <Card className={`shadow-sm ${summary?.overdue_count > 0 ? 'bg-destructive/5 border-destructive/30' : ''}`}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Overdue</CardTitle>
            <AlertCircle className={`h-4 w-4 ${summary?.overdue_count > 0 ? 'text-destructive' : 'text-muted-foreground'}`} />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${summary?.overdue_count > 0 ? 'text-destructive' : ''}`} data-testid="overdue-count">
              {summary?.overdue_count || 0}
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Due This Week</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="due-week-count">{summary?.due_this_week_count || 0}</div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Total Due</CardTitle>
            <CircleDollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="total-due">{formatINR(summary?.total_due || 0, { compact: true })}</div>
          </CardContent>
        </Card>
        <Card className="shadow-sm bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-900">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Total Paid</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600 dark:text-green-400" data-testid="total-paid">
              {formatINR(summary?.total_paid || 0, { compact: true })}
            </div>
          </CardContent>
        </Card>
      </div>

      {vendors.length === 0 && (
        <Card className="shadow-sm border-secondary/30 bg-secondary/5">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-secondary-foreground" />
            <p className="text-sm">Add vendors first before scheduling payments. <a href="/vendors" className="text-primary hover:underline">Go to Vendors</a></p>
          </CardContent>
        </Card>
      )}

      {payments.length === 0 && vendors.length > 0 ? (
        <Card className="shadow-sm">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <DollarSign className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">No payments scheduled yet. Add your first payment schedule!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {overdue.length > 0 && (
            <div>
              <h2 className="text-sm font-medium uppercase tracking-[0.2em] text-destructive mb-3">Overdue ({overdue.length})</h2>
              <div className="space-y-2">{overdue.map(renderPayment)}</div>
            </div>
          )}
          {dueThisWeek.length > 0 && (
            <div>
              <h2 className="text-sm font-medium uppercase tracking-[0.2em] text-muted-foreground mb-3">Due This Week ({dueThisWeek.length})</h2>
              <div className="space-y-2">{dueThisWeek.map(renderPayment)}</div>
            </div>
          )}
          {upcoming.length > 0 && (
            <div>
              <h2 className="text-sm font-medium uppercase tracking-[0.2em] text-muted-foreground mb-3">Upcoming ({upcoming.length})</h2>
              <div className="space-y-2">{upcoming.map(renderPayment)}</div>
            </div>
          )}
          {paid.length > 0 && (
            <div>
              <h2 className="text-sm font-medium uppercase tracking-[0.2em] text-green-700 dark:text-green-400 mb-3">Paid ({paid.length})</h2>
              <div className="space-y-2">{paid.map(renderPayment)}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
